import { useEffect, useRef, useState } from "react";
import styles from "./DealerOnboarding.module.css";
import apiService from "../../services/apiService";
import { getPreviewUrlFromRemote } from "../../utils/imagePreview";

const fileToBase64 = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
  });
};

const isHeicLikeUrl = (url = "") => {
  const lower = url.toLowerCase();
  return (
    lower.endsWith(".heic") ||
    lower.endsWith(".heif") ||
    lower.includes(".heic?") ||
    lower.includes(".heif?")
  );
};

const ImageWithHeicSupport = ({
  src,
  alt,
  className,
  style,
  onClick,
}) => {
  const [displayUrl, setDisplayUrl] = useState(src);
  const [loading, setLoading] = useState(false);
  const convertedUrlRef = useRef(null);

  useEffect(() => {
    if (!src) {
      setDisplayUrl(null);
      return;
    }

    const lowerSrc = typeof src === "string" ? src.toLowerCase() : "";
    const shouldConvert = isHeicLikeUrl(lowerSrc);

    if (shouldConvert) {
      setLoading(true);
      getPreviewUrlFromRemote(src)
        .then((convertedUrl) => {
          if (
            convertedUrlRef.current &&
            convertedUrlRef.current.startsWith("blob:")
          ) {
            URL.revokeObjectURL(convertedUrlRef.current);
          }
          convertedUrlRef.current = convertedUrl;
          setDisplayUrl(convertedUrl);
        })
        .catch((error) => {
          console.error("Failed to process HEIC image:", error);
          setDisplayUrl(src);
        })
        .finally(() => setLoading(false));
    } else {
      setDisplayUrl(src);
    }

    return () => {
      if (
        convertedUrlRef.current &&
        convertedUrlRef.current.startsWith("blob:")
      ) {
        URL.revokeObjectURL(convertedUrlRef.current);
      }
    };
  }, [src]);

  if (loading) {
    return (
      <div className={styles.heicLoader}>
        <div className={styles.spinnerSmall} />
        <span>Loading…</span>
      </div>
    );
  }

  if (!displayUrl) {
    return null;
  }

  return (
    <img
      src={displayUrl}
      alt={alt}
      className={className}
      style={style}
      onClick={onClick}
    />
  );
};

export default function DealerDetailsModal({ dealer, onClose }) {
  const [activeTab, setActiveTab] = useState("details");
  const [comments, setComments] = useState({});
  const [stageStates, setStageStates] = useState(
    dealer.stage_validation_states
  );
  const [previewDoc, setPreviewDoc] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [paymentLoading, setPaymentLoading] = useState(false);
  const inputRef = useRef(null);
  // Handler to check payment status
  const handleCheckPaymentStatus = async () => {
    setPaymentLoading(true);
    setPaymentStatus(null);
    try {
      const response = await apiService.post("dealerOnboardingPayment", {
        dealer_id: dealer.uid,
        action: "check_status",
      });
      console.log("Payment status response:", response);
      setPaymentStatus(response);
    } catch (error) {
      setPaymentStatus("Error checking payment status");
    } finally {
      setPaymentLoading(false);
    }
  };

  const TABS = [
    { key: "details", label: "Dealer Details" },
    { key: "payment", label: "Payment" },
    { key: "documents", label: "Documents" },
    { key: "contract", label: "Contract" },
  ];

  const handleCreateContract = async (e) => {
    e.preventDefault();
    if (!inputRef.current) return;

    const file = inputRef.current?.files[0];
    if (!file) {
      alert("Please select a file to upload.");
      return;
    }

    try {
      const base64File = await fileToBase64(file);
      const fileBase64Data = base64File.split(",")[1];
      const response = await apiService.post("createDealerContract", {
        dealerId: dealer.uid,
        file: fileBase64Data,
        metadata: {
          contentType: file.type,
          originalName: file.name,
          size: file.size,
        },
      });

      if (response.error) {
        alert("Failed to create contract: " + response.message);
        return;
      }

      alert("Contract created successfully.");
    } catch (error) {
      console.error("Error creating contract:", error);
      alert("An unexpected error occurred while creating contract.");
    }
  };

  const handleValidate = async (tab, newStatus) => {
    try {
      const response = await apiService.post("updateDealerStatus", {
        dealerId: dealer.uid,
        stage: tab,
        status: newStatus,
        comments: comments[tab] || "",
      });

      if (response.error) {
        alert("Failed to update dealer status: " + response.message);
        return;
      }

      setStageStates((prev) => ({
        ...prev,
        [tab]: {
          ...prev[tab],
          status: newStatus,
          comment: comments[tab] || "",
        },
      }));
    } catch (error) {
      console.error("Error updating dealer status:", error);
      alert("An unexpected error occurred while updating dealer status.");
    }
  };

  const renderDetailsByType = (details) => {
    const base = (
      <p>
        <strong>Mailing Address:</strong> {details.mailing_address}
      </p>
    );

    switch (details.company_type) {
      case "proprietorship":
      case "individual":
        return (
          <>
            <p>
              <strong>Owner Name:</strong> {details.name}
            </p>
            <p>
              <strong>Business Name:</strong> {details.business_name}
            </p>
            <p>
              <strong>PAN Number:</strong> {details.pan_number}
            </p>
            <p>
              <strong>Aadhar Number:</strong> {details.aadhaar_number}
            </p>
            {base}
          </>
        );
      case "limited_liability_partnership":
        return (
          <>
            <p>
              <strong>Business Name:</strong> {details.business_name}
            </p>
            <p>
              <strong>Company PAN:</strong> {details.company_pan_number}
            </p>
            <p>
              <strong>Partners:</strong> {details.partners?.join(", ")}
            </p>
            {base}
          </>
        );
      default:
        return (
          <>
            <p>
              <strong>Business Name:</strong> {details.business_name}
            </p>
            {details.company_pan_number && (
              <p>
                <strong>Company PAN:</strong> {details.company_pan_number}
              </p>
            )}
            {base}
          </>
        );
    }
  };

  const renderDocuments = (docs) => {
    const companyDocs = Object.entries(docs).filter(
      ([key]) => key !== "signatories"
    );
    const signatories = docs.signatories || [];

    return (
      <>
        <h4>Company Documents</h4>
        <div className={styles.docGrid}>
          {companyDocs.map(([key, doc]) => (
            <div
              key={key}
              className={styles.docCard}
              onClick={() => setPreviewDoc({ url: doc.url, label: key })}
            >
              <div className={styles.docThumb}>
                {doc.url?.toLowerCase().endsWith(".pdf") ? (
                  <span>📄</span>
                ) : (
                  <ImageWithHeicSupport
                    src={doc.url}
                    alt={key}
                    className={styles.docImageThumb}
                  />
                )}
              </div>

              <p>{key.replaceAll("_", " ")}</p>
            </div>
          ))}
        </div>

        {signatories.length > 0 && (
          <>
            <h4>Authorized Signatories</h4>
            {signatories.map((sig, idx) => (
              <div key={idx} className={styles.signatorySection}>
                <p>
                  <strong>{sig.name}</strong>
                </p>
                <div className={styles.docGrid}>
                  {Object.entries(sig.docs).map(([docKey, doc]) => (
                    <div
                      key={docKey}
                      className={styles.docCard}
                      onClick={() =>
                        setPreviewDoc({ url: doc.url, label: docKey })
                      }
                    >
                      <div className={styles.docThumb}>
                        {doc.url?.toLowerCase().endsWith(".pdf") ? (
                          <span>📄</span>
                        ) : (
                          <ImageWithHeicSupport
                            src={doc.url}
                            alt={docKey}
                            className={styles.docImageThumb}
                          />
                        )}
                      </div>
                      <p>{docKey.replaceAll(/_|signatory_[0-9]/gm, " ")}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </>
        )}
      </>
    );
  };

  const renderContract = () => {
    const stages = ["details", "payment", "documents"];
    const anyPending = stages.some(
      (stage) =>
        stageStates[stage]?.status === "pending" ||
        stageStates[stage]?.status === "not_valid"
    );
    if (anyPending) {
      return (
        <div style={{ textAlign: "center" }}>
          Previous Stages must be validated
        </div>
      );
    }
    // If all previous stages are validated, the user must upload the contract, which the dealer will download
    const allValidated = stages.every(
      (stage) => stageStates[stage]?.status === "validated"
    );
    if (allValidated && stageStates["contract"]?.status === "pending") {
      return (
        <div style={{ textAlign: "center" }}>
          Upload the contract document for the dealer to download and sign.
          <input ref={inputRef} type="file" accept=".pdf,.doc,.docx" />
          <button onClick={handleCreateContract}>Create Contract</button>
        </div>
      );
    } else if (stageStates["contract"]?.status === "created") {
      return (
        <div style={{ textAlign: "center" }}>
          Contract has been created. Awaiting for dealer to download.
        </div>
      );
    } else if (stageStates["contract"]?.status === "downloaded") {
      return (
        <div style={{ textAlign: "center" }}>
          Dealer has downloaded the contract. Awaiting signed contract upload.
        </div>
      );
    } else if (stageStates["contract"]?.status === "validation_pending") {
      return (
        <div style={{ textAlign: "center" }}>
          Signed contract uploaded by dealer. Please validate the contract.
          {/* View Contract link */}
          <div style={{ marginTop: "1rem" }}>
            <button
              onClick={() => {
                // Assuming dealer.contract.url contains the URL to the contract
                const contractUrl = dealer.contract?.signed_url;
                if (contractUrl) {
                  window.open(contractUrl, "_blank");
                } else {
                  alert("Contract URL not available.");
                }
              }}
            >
              Download Signed Contract
            </button>
          </div>
          <textarea
            placeholder="Add reviewer comments for this stage..."
            value={comments[activeTab] || ""}
            onChange={(e) =>
              setComments({ ...comments, [activeTab]: e.target.value })
            }
            className={styles.commentBox}
          />
          <div className={styles.validationButtons}>
            <button
              className={styles.rejectBtn}
              onClick={() => handleValidate(activeTab, "not_valid")}
            >
              Mark Invalid
            </button>
            <button
              className={styles.approveBtn}
              onClick={() => handleValidate(activeTab, "validated")}
            >
              Validate
            </button>
          </div>
        </div>
      );
    } else if (stageStates["contract"]?.status === "validated") {
      return (
        <div style={{ textAlign: "center" }}>Contract has been validated.</div>
      );
    }
    return <div></div>;
  };

  // Check if the current active stage is pending
  const activeStagePending = stageStates[activeTab]?.status === "pending";

  return (
    <>
      <div className={styles.modalBackdrop}>
        <div className={styles.modalLarge}>
          <button className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
          <h2>{dealer.name}</h2>

          {/* Tabs */}
          <div className={styles.tabHeader}>
            {TABS.map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`${styles.tabBtn} ${
                  activeTab === tab.key ? styles.activeTab : ""
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className={styles.tabContent}>
            {activeTab === "details" && dealer.details && (
              <>
                <h3>Dealer Details</h3>
                {renderDetailsByType(dealer.details)}
              </>
            )}

            {activeTab === "payment" &&
              ["pending", "validation_pending", "success"].includes(
                dealer.stage_validation_states?.payment?.status
              ) && (
                <div style={{ marginTop: "1rem" }}>
                  <button
                    onClick={handleCheckPaymentStatus}
                    disabled={paymentLoading}
                  >
                    {paymentLoading
                      ? "Checking..."
                      : "Check Payment Status"}
                  </button>
                  {paymentStatus && (
                    <>
                      <div style={{ marginTop: "0.5rem", fontWeight: 500 }}>
                        Payment Status: {paymentStatus.payment_status ?? paymentStatus.message ?? "No status available"}
                      </div>
                      <div style={{ marginTop: "0.5rem", fontWeight: 500 }}>
                        Cities: {paymentStatus.order_info?.notes?.cities_ids}
                      </div>
                      <div style={{ marginTop: "0.5rem", fontWeight: 500 }}>
                        Amount: {(paymentStatus.order_info?.amount ?? 0) / 100}{" "}
                        INR
                      </div>
                    </>
                  )}
                </div>
              )}

            {activeTab === "payment" && dealer.payment && (
              <>
                <h3>Payment Information</h3>
                <p>
                  <strong>Status:</strong> {dealer.payment.status}
                </p>
                {dealer.payment.verified_at && (
                  <p>
                    <strong>Verified At:</strong>{" "}
                    {new Date(dealer.payment.verified_at).toLocaleString()}
                  </p>
                )}
              </>
            )}

            {activeTab === "documents" &&
              dealer.documents &&
              renderDocuments(dealer.documents)}

            {/* {activeTab === "contract" && (
              // TODO: Implement contract
            )} */}

            {/* Validation Controls */}
            <div className={styles.validationBox}>
              {activeTab === "contract" ? (
                renderContract()
              ) : activeStagePending ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem 0",
                    fontWeight: 500,
                  }}
                >
                  Pending dealer action
                </div>
              ) : dealer.stage_validation_states[activeTab].status ===
                "not_valid" ? (
                <div className={styles.previousComments}>
                  <h5>Waiting for dealer to resolve. Previous comments:</h5>
                  <p>{dealer.stage_validation_states[activeTab].comments}</p>
                </div>
              ) : dealer.stage_validation_states[activeTab].status ===
                "validated" ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "2rem 0",
                    fontWeight: 500,
                    color: "green",
                  }}
                >
                  This stage has been validated.
                </div>
              ) : (
                <>
                  <textarea
                    placeholder="Add reviewer comments for this stage..."
                    value={comments[activeTab] || ""}
                    onChange={(e) =>
                      setComments({ ...comments, [activeTab]: e.target.value })
                    }
                    className={styles.commentBox}
                  />
                  <div className={styles.validationButtons}>
                    <button
                      className={styles.rejectBtn}
                      onClick={() => handleValidate(activeTab, "not_valid")}
                    >
                      Mark Invalid
                    </button>
                    <button
                      className={styles.approveBtn}
                      onClick={() => handleValidate(activeTab, "validated")}
                    >
                      Validate
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Document Preview Modal */}
      {previewDoc && (
        <div className={styles.previewBackdrop}>
          <div className={styles.previewModal}>
            <button
              className={styles.closePreview}
              onClick={() => setPreviewDoc(null)}
            >
              ✕
            </button>
            <h3>{previewDoc.label.replaceAll("_", " ")}</h3>

            {previewDoc.url?.toLowerCase().endsWith(".pdf") ? (
              <div className={styles.pdfPreviewNotice}>
                PDF preview not available. Please download to view.
              </div>
            ) : (
              <ImageWithHeicSupport
                src={previewDoc.url}
                alt={previewDoc.label}
                className={styles.previewImage}
              />
            )}

            <button
              className={styles.openDocBtn}
              style={{
                cursor: "pointer",
                marginTop: "1rem",
                padding: "0.5rem 1rem",
                fontWeight: "600",
                background: "#3182ce",
                color: "white",
                border: "none",
                borderRadius: "5px",
              }}
              onClick={() => window.open(previewDoc.url, "_blank")}
            >
              Download Document
            </button>
          </div>
        </div>
      )}
    </>
  );
}
